import { atom as f, map as b, computed as v } from "nanostores";
import { copilotTools as F, copilotInstructions as I, copilotContext as j, uniqueId as x } from "./global-state.js";
function w() {
  return Math.floor(Date.now() / 1e3);
}
const M = {
  idle: ["initializing", "ending"],
  initializing: ["ready", "error"],
  ready: ["sending", "ending"],
  sending: ["receiving"],
  receiving: ["ready", "reconnecting"],
  reconnecting: ["ready", "ending"],
  ending: ["ended", "error"],
  error: ["initializing", "ending"],
  ended: []
  // Can only transition to itself (terminal state)
};
function A(r, e) {
  if (r === e)
    return r;
  if (!M[r])
    throw new Error(`Unknown status: ${r}`);
  if (M[r].includes(e))
    return e;
  throw new Error(`Invalid status transition: ${r} -> ${e}`);
}
class K {
  constructor(e) {
    this.__id = f(void 0), this.config = {
      includeGlobalContext: !0,
      includeGlobalTools: !0,
      includeGlobalInstructions: !0
    }, this.__status = f("idle"), this.__messages = f([]), this.__meta = f({}), this.__uploadFiles = f([]), this.__queuedMessages = f([]), this.__abortController = null, this.__enableWebSearch = f(!0), this.__serverSideTools = b({}), this.__sessionInstructions = b({}), this.__sessionTools = b({}), this.__sessionContext = b({}), this.__tools = v(
      [this.__serverSideTools, F, this.__sessionTools],
      (t, o, a) => {
        const i = {}, s = {
          ...this.config.includeGlobalTools === !0 ? o : {},
          ...a
        };
        for (const n of Object.values(t))
          if (n != null) {
            const c = s[n.name];
            i[n.name] = {
              ...c?.type === "server" ? c : void 0,
              ...n
            };
          }
        for (const n of Object.values(s))
          n != null && n.type === "client" && i[n.name] == null && (i[n.name] = n);
        return i;
      }
    ), this.__statefulMessages = v([this.__messages, this.__tools], U), this.__instructions = v(
      [I, this.__sessionInstructions],
      (t, o) => ({
        ...this.config.includeGlobalInstructions === !0 ? t : {},
        ...o
      })
    ), this.__context = v([j, this.__sessionContext], (t, o) => ({
      ...this.config.includeGlobalContext === !0 ? t : {},
      ...o
    })), this.__suggestionsRefreshTrigger = f([]), e && (this.config = { ...this.config, ...e });
  }
  get id() {
    return this.__id.get();
  }
  set id(e) {
    this.__id.set(e != null ? String(e) : void 0);
  }
  /**
   * The current status of the Copilot session.
   */
  get status() {
    return this.__status.get();
  }
  /**
   * If the session is being ended.
   */
  get isEnding() {
    return this.status === "ending";
  }
  get isEnded() {
    return this.status === "ended";
  }
  /**
   * Sets the status of the Copilot session.
   */
  setStatus(e) {
    this.__status.set(A(this.status, e));
  }
  /**
   * adds an instruction just for this session
   */
  addInstruction(e) {
    const t = x();
    return this.__sessionInstructions.setKey(t, e), () => {
      this.__sessionInstructions.setKey(t, void 0);
    };
  }
  /**
   * Adds a tool just for this session
   */
  addTool(e) {
    return this.__sessionTools.setKey(e.name, e), () => {
      this.__sessionTools.setKey(e.name, void 0);
    };
  }
  /**
   * Adds a context just for this session
   */
  addContext(e) {
    const t = x();
    return this.__sessionContext.setKey(t, e), () => {
      this.__sessionContext.setKey(t, void 0);
    };
  }
  /**
   * Sets the meta data for this session
   */
  setSessionMeta(e) {
    this.__meta.set(e);
  }
  /**
   * Gets the meta data for this session
   */
  getSessionMeta() {
    return this.__meta.get();
  }
  /**
   * Sets whether the model can search the web to answer questions.
   */
  setWebSearchEnabled(e) {
    this.__enableWebSearch.set(e);
  }
  /**
   * Gets whether web search is enabled for this session.
   */
  getWebSearchEnabled() {
    return this.__enableWebSearch.get();
  }
  /**
   * Updates the meta data for a specific message by targeting message.meta.id
   */
  setMessageMeta(e, t) {
    const a = this.__messages.get().map((i) => i.role === "model" && i.type === "meta" && "meta" in i && i.meta?.id === e ? {
      ...i,
      meta: t
    } : i);
    this.__messages.set(a);
  }
  /**
   * Clears any messages that are queued to be sent after the current response completes.
   * Returns the messages that were cleared so they can be restored if needed.
   */
  clearQueuedMessages() {
    const e = this.__queuedMessages.get();
    return this.__queuedMessages.set([]), e;
  }
  /**
   * Stops the current in-flight exchange.
   *
   * Aborts the streaming HTTP request immediately (so the UI becomes responsive
   * right away) and fires a request to cancel the Temporal workflow on the
   * backend (so it stops burning LLM tokens).
   *
   * Always returns `{ messages }` — the messages that were queued while the
   * exchange was running — so the caller can restore them to the input box
   * regardless of whether the backend call succeeded. If the backend call
   * fails, `error` is also set so the caller can surface it to the user.
   *
   * Never throws. Safe to call in any state.
   */
  async stop() {
    if (this.status !== "sending" && this.status !== "receiving" && this.status !== "reconnecting")
      return { messages: [] };
    if (this.status === "reconnecting") {
      const o = this.clearQueuedMessages();
      this.setStatus("ready");
      const a = window.__hydra_copilot__?.stopExchange;
      if (a != null && this.id != null)
        try {
          await a({ id: this.id });
        } catch (i) {
          return {
            messages: o,
            error: i instanceof Error ? i : new Error(String(i))
          };
        }
      return { messages: o };
    }
    this.__abortController?.abort();
    const e = this.clearQueuedMessages(), t = window.__hydra_copilot__?.stopExchange;
    if (t != null && this.id != null)
      try {
        await t({ id: this.id });
      } catch (o) {
        return {
          messages: e,
          error: o instanceof Error ? o : new Error(String(o))
        };
      }
    return { messages: e };
  }
  /**
   * Forces a refresh of suggestions for this session.
   * This will trigger any active suggestion hooks to regenerate their suggestions.
   * @param instruction - Custom instructions to include in the next suggestions fetch.
   * These instructions will be used once and then cleared.
   */
  refreshSuggestions(e) {
    this.__suggestionsRefreshTrigger.set([{ instruction: e }]);
  }
  /**
   * Initializes a new session, either from an existing session id,
   * or by creating a new one
   */
  async initialize(e) {
    if (this.status !== "idle" && this.status !== "error")
      return this.id;
    this.setStatus("initializing");
    try {
      const t = window.__hydra_copilot__?.startSession;
      if (t == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      if (e == null) {
        const o = await t();
        if (o.id == null)
          throw new Error("Failed to start session, no session ID returned");
        this.id = o.id, this.__serverSideTools.set(E(o.tools ?? []));
      } else {
        this.id = e;
        const o = await this.loadSession();
        this.__messages.set(o.messages ?? []), this.__meta.set(o.meta ?? {}), this.__serverSideTools.set(E(o.tools ?? []));
      }
      return this.setStatus("ready"), this.id;
    } catch (t) {
      throw this.setStatus("error"), t;
    }
  }
  /**
   * Ends the current session, clearing all messages and state.
   */
  async end() {
    this.setStatus("ending");
    try {
      const e = window.__hydra_copilot__?.endSession;
      if (e == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      this.id != null && await e(this.id), this.__messages.set([]), this.__queuedMessages.set([]), this.id = void 0, this.__sessionInstructions.set({}), this.__sessionTools.set({}), this.__sessionContext.set({}), this.__serverSideTools.set({}), this.__meta.set({}), this.setStatus("ended");
    } catch (e) {
      throw this.setStatus("error"), e;
    }
  }
  /**
   * Returns pending approval tool calls from the session's messages.
   * A pending approval is a tool call with `needs_approval === true` that
   * doesn't yet have a corresponding tool_response message.
   */
  getPendingApprovals() {
    const e = this.__messages.get(), t = /* @__PURE__ */ new Set();
    for (const a of e)
      if (a.role === "user" && a.type === "tool_response")
        for (const i of a.tool_responses)
          t.add(i.tool_call_id);
    const o = [];
    for (const a of e)
      if (a.role === "model" && a.type === "tool_call")
        for (const i of a.tool_calls)
          i.needs_approval === !0 && !t.has(i.id) && o.push({ id: i.id, name: i.name });
    return o;
  }
  async sendMessage(e) {
    let t = typeof e == "string" ? [{ role: "user", type: "text", text: e, timestamp: w() }] : [...e];
    const o = window.__hydra_copilot__?.sendStreamingMessage;
    if (o == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.status === "idle" && await this.initialize(), this.status !== "ready") {
      if (this.status === "sending" || this.status === "receiving" || this.status === "reconnecting") {
        this.__queuedMessages.set([...this.__queuedMessages.get(), ...t]);
        return;
      }
      throw new Error(`Cannot send message while session is in "${this.status}" state.`);
    }
    if (!t.some((n) => n.type === "tool_response")) {
      const n = this.getPendingApprovals();
      n.length > 0 && (t = [O(n), ...t]);
    }
    let i = null, l = 0, s = !1;
    try {
      this.setStatus("sending"), this.__abortController = new AbortController();
      let n = t.map((c) => ({
        ...c,
        timestamp: c.timestamp || w()
      }));
      for (; n.length > 0; ) {
        this.__messages.set([...this.__messages.get(), ...n]);
        const c = [], _ = (p) => {
          if (this.setStatus("receiving"), l += 1, p.type === "meta" && (s = !0), this.__messages.set([...this.__messages.get(), p]), p.role === "model" && p.type == "tool_call")
            for (const u of p.tool_calls) {
              if (u.needs_approval || u.is_client_tool !== !0)
                continue;
              const m = this.__tools.get()[u.name];
              c.push(
                (async () => {
                  try {
                    if (m?.type === "server")
                      throw new Error(
                        `Tool ${u.name} is a server-side tool and cannot be called on the client.`
                      );
                    if (m?.handler == null)
                      throw new Error(`Tool ${u.name} does not have a handler defined.`);
                    const y = await m.handler({ args: u.args, session: this, toolCallId: u.id }) ?? null;
                    return {
                      tool_call_id: u.id,
                      name: u.name,
                      response: { result: y, error: null },
                      finished: !0
                    };
                  } catch (y) {
                    return {
                      tool_call_id: u.id,
                      name: u.name,
                      response: {
                        result: null,
                        error: `Error calling tool ${u.name}: ${y instanceof Error ? y.message : String(y)}`
                      },
                      finished: !0
                    };
                  }
                })()
              );
            }
          if (p.role === "user" && p.type === "tool_response")
            for (const u of p.tool_responses) {
              const m = this.__tools.get()[u.name], y = z(
                [...this.__messages.get(), ...n],
                u.tool_call_id
              );
              m?.type === "server" && m?.handler != null && u.finished === !0 && m.handler({
                args: y?.args || {},
                session: this,
                response: u.response,
                toolCallId: u.tool_call_id,
                wasApproved: u.was_approved === !0
              });
            }
        }, d = {
          id: this.id,
          messages: n,
          tools: R(this.__tools.get()),
          instructions: P(this.__instructions.get()),
          context: q(this.__context.get()),
          onMessage: _,
          signal: this.__abortController?.signal,
          enable_web_search: this.getWebSearchEnabled()
        };
        if (await o(d), c.length === 0) {
          n = [];
          continue;
        }
        n = [
          {
            role: "user",
            type: "tool_response",
            tool_responses: await Promise.all(c),
            timestamp: w()
          }
        ];
      }
      if (l === 0)
        throw new Error("No messages received from the server. Please check your connection.");
      if (l > 0 && !s) {
        this.setStatus("reconnecting"), this.recoverInterruptedStream().catch(() => {
          this.status === "reconnecting" && this.setStatus("ready");
        });
        return;
      }
      this.setStatus("ready"), i = C(this.__queuedMessages);
    } catch (n) {
      if (this.setStatus("receiving"), this.setStatus("ready"), D(n))
        return;
      throw n;
    } finally {
      this.__abortController = null;
    }
    i != null && i.length > 0 && this.sendMessage(i).catch(() => {
    });
  }
  /**
   * Attempts to recover from an interrupted stream by reloading the session
   * from the backend. The backend continues the LLM call without streaming
   * when the connection drops, so the full response is eventually persisted.
   *
   * Retries with increasing delays to give the workflow time to complete.
   * On success, replaces local messages with the server state and transitions
   * to 'ready'. On failure, appends an error message and transitions to 'ready'.
   */
  async recoverInterruptedStream() {
    if (this.id == null || window.__hydra_copilot__?.getSession == null) {
      this.setStatus("ready");
      return;
    }
    const e = [2e3, 3e3, 5e3, 5e3, 1e4];
    for (const t of e) {
      if (await new Promise((i) => setTimeout(i, t)), this.status !== "reconnecting")
        return;
      let o;
      try {
        const i = await this.loadSession();
        o = { messages: i.messages ?? [], meta: i.meta ?? {} };
      } catch {
        continue;
      }
      if (o.messages[o.messages.length - 1]?.type === "meta") {
        if (this.status !== "reconnecting")
          return;
        this.__messages.set(o.messages), this.__meta.set(o.meta), this.setStatus("ready"), this.drainAndSendQueuedMessages();
        return;
      }
    }
    this.status === "reconnecting" && (this.__messages.set([
      ...this.__messages.get(),
      {
        role: "model",
        type: "error",
        error: "The connection was interrupted. Please try sending your message again.",
        timestamp: w()
      }
    ]), this.setStatus("ready"));
  }
  /**
   * Drains the message queue and sends the combined messages.
   * Combines text messages into a single message to avoid flooding the LLM.
   */
  drainAndSendQueuedMessages() {
    const e = C(this.__queuedMessages);
    e.length !== 0 && this.sendMessage(e).catch(() => {
    });
  }
  /**
   * Loads the session data (messages and meta)
   */
  async loadSession() {
    const e = window.__hydra_copilot__?.getSession;
    if (e == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.id == null)
      throw new Error("Session not started");
    return await e(this.id);
  }
  /**
   * Waits for the session to be ready
   */
  async waitUntilReady() {
    if (this.status !== "ready") {
      if (this.status === "idle") {
        await this.initialize();
        return;
      }
      if (this.status === "initializing")
        return new Promise((e, t) => {
          let o = null, a = null;
          const i = () => {
            o !== null && (clearTimeout(o), o = null), a !== null && (a(), a = null);
          };
          a = this.__status.subscribe((l) => {
            l === "ready" ? (i(), e()) : l === "error" ? (i(), t(new Error("Session initialization failed"))) : l !== "initializing" && (i(), t(new Error(`Unexpected session status: ${l}`)));
          }), o = setTimeout(() => {
            i(), t(new Error("Session initialization timeout - exceeded 30 seconds"));
          }, 3e4);
        });
      throw this.status === "error" ? new Error("Session is in error state") : new Error(`Cannot wait for session in "${this.status}" state`);
    }
  }
  /**
   * Gets upload signature for file upload
   * Note: Session readiness is now checked at the upload level (uploadCopilotFile)
   */
  async getUploadSignature(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getUploadSignature");
    const t = window.__hydra_copilot__;
    if (!t?.getUploadSignature)
      throw new Error("File upload not configured. Please provide getUploadSignature in setCopilotConfig.");
    return await t.getUploadSignature({
      id: this.id,
      size: e.size,
      mime_type: e.mime_type
    });
  }
  /**
   * Gets download URL for a previously uploaded file
   */
  async getFileDownloadUrl(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getFileDownloadUrl");
    const t = window.__hydra_copilot__;
    if (!t?.getFileDownloadUrl)
      throw new Error("File download not configured. Please provide getFileDownloadUrl in setCopilotConfig.");
    return (await t.getFileDownloadUrl({
      id: this.id,
      filename: e
    })).url;
  }
  /**
   * Add files to the upload queue
   */
  addUploadFiles(e) {
    const t = this.__uploadFiles.get();
    this.__uploadFiles.set([...t, ...e]);
  }
  /**
   * Update a specific upload file
   */
  updateUploadFile(e, t) {
    const a = this.__uploadFiles.get().map((i) => i.id === e ? { ...i, ...t } : i);
    this.__uploadFiles.set(a);
  }
  /**
   * Remove upload file
   */
  removeUploadFile(e) {
    const o = this.__uploadFiles.get().filter((a) => a.id !== e);
    this.__uploadFiles.set(o);
  }
  /**
   * Clear all upload files
   */
  clearUploadFiles() {
    this.__uploadFiles.set([]);
  }
}
function T(r) {
  return typeof r == "string" ? r : typeof r == "number" || typeof r == "boolean" ? String(r) : Array.isArray(r) || typeof r == "object" ? JSON.stringify(r) : String(r);
}
function U(r, e) {
  const t = [];
  let o = [];
  const a = {}, i = r.filter(
    (s) => {
      if (s.role === "user" && s.type === "tool_response") {
        for (const n of s.tool_responses)
          n.tool_call_id != null && (a[n.tool_call_id] == null && (a[n.tool_call_id] = []), a[n.tool_call_id].push(n));
        return !1;
      }
      return s.type === "text" || s.type === "content" || s.type === "tool_call" || s.type === "meta" || s.type === "error" || s.type === "compaction_start" || s.type === "compaction_complete";
    }
  );
  function l() {
    const s = o.filter((h) => h.type === "text"), n = o.find((h) => h.type === "meta" && "meta" in h);
    if (o = [], s.length === 0)
      return;
    const c = s.map((h) => h.text).join(""), _ = n?.meta ?? {}, d = s[0]?.thought;
    let g;
    if (d === !0) {
      const h = t[t.length - 1];
      h != null && h.timestamp != null ? g = Math.max(0, s[s.length - 1].timestamp - h.timestamp) : g = 0;
    }
    t.push({
      role: "model",
      type: "text",
      text: c,
      timestamp: s[0].timestamp,
      meta: _,
      ...d === !0 && { thought: d },
      ...g != null && { duration: g }
    }), o = [];
  }
  for (const s of i)
    if (s.role === "model" && s.type === "text") {
      const n = o.some((d) => d.type === "text"), c = s.thought === !0, _ = o.some(
        (d) => d.type === "text" && "thought" in d && d.thought === !0
      );
      n && (c && !_ || !c && _) && l(), o.push(s);
    } else if (s.role === "user" && (s.type === "text" || s.type === "content"))
      l(), t.push(s);
    else if (s.role === "model" && s.type === "meta")
      o.push(s), l();
    else if (s.role === "model" && s.type === "tool_call")
      l(), t.push({
        role: "model",
        type: "tool_call",
        tool_calls: s.tool_calls.map((n) => {
          const c = a[n.id], _ = e[n.name];
          if (c == null || c.length === 0)
            return {
              ...n,
              status: "executing"
            };
          const d = c.find((u) => u.response.error != null && u.response.error !== ""), g = c.some((u) => u.finished), h = c.some((u) => u.was_approved === !0);
          if (d != null)
            return {
              ...n,
              status: "error",
              response: d.response,
              ...h && { was_approved: !0 }
            };
          const p = _?.type === "server" && _?.isStreaming === !0;
          return g && c.length === 1 && !p ? {
            ...n,
            status: "success",
            response: c[0].response,
            ...h && { was_approved: !0 }
          } : {
            ...n,
            status: g ? "success" : "executing",
            response: {
              result: c.map((u) => u.response.result),
              error: null
            },
            ...h && { was_approved: !0 }
          };
        }),
        timestamp: s.timestamp
      });
    else if (s.role === "model" && s.type === "compaction_start")
      l(), t.push({
        role: "model",
        type: "compaction",
        status: "compacting",
        timestamp: s.timestamp
      });
    else if (s.role === "model" && s.type === "compaction_complete") {
      l();
      const n = t.findIndex(
        (_) => _.role === "model" && _.type === "compaction" && _.status === "compacting"
      ), c = {
        role: "model",
        type: "compaction",
        status: "complete",
        summary: s.text,
        timestamp: s.timestamp
      };
      n >= 0 ? t[n] = c : t.push(c);
    } else
      s.role === "model" && s.type === "error" ? (l(), t.push(s)) : console.error("Unknown message type", s);
  l();
  for (let s = 0; s < t.length; s++) {
    const n = t[s];
    if (n.role !== "model" || n.type !== "tool_call" || !n.tool_calls.some((d) => d.status === "executing"))
      continue;
    t.slice(s + 1).some((d) => d.role === "model" && (d.type === "text" || d.type === "tool_call" || d.type === "error")) && (t[s] = {
      ...n,
      tool_calls: n.tool_calls.map((d) => d.status === "executing" ? { ...d, status: "success" } : d)
    });
  }
  return t;
}
function R(r) {
  return Object.values(r).filter((e) => e !== void 0 && e.type === "client").map((e) => ({
    name: e.name,
    description: typeof e.description == "string" ? S(e.description) : e.description,
    request_json_schema: e.requestSchema,
    json_response_schema: e.responseSchema
  }));
}
function P(r) {
  return Array.isArray(r) ? r.map((e) => S(e.instruction).trim()) : Object.values(r).filter((e) => e !== void 0).map((e) => S(e.instruction).trim());
}
function q(r) {
  return Array.isArray(r) ? r.map((e) => ({
    description: S(e.description),
    value: T(typeof e.value == "function" ? e.value() : e.value)
  })) : Object.values(r).filter((e) => e !== void 0).map((e) => ({
    description: S(e.description),
    value: T(typeof e.value == "function" ? e.value() : e.value)
  }));
}
function S(r) {
  const e = r.split(`
`), t = e.filter((l) => l.trim().length > 0);
  if (t.length === 0)
    return r;
  const o = t.map((l) => {
    const s = l.match(/^(\s*)/);
    return s ? s[1].length : 0;
  }), a = Math.min(...o);
  return e.map((l) => l.trim().length === 0 ? l : l.slice(a)).join(`
`);
}
function z(r, e) {
  for (const t of r)
    if (t.role === "model" && t.type === "tool_call") {
      const o = t.tool_calls.find((a) => a.id === e);
      if (o)
        return o;
    }
}
function C(r) {
  const e = r.get();
  if (e.length === 0)
    return [];
  r.set([]);
  const t = e.filter((i) => i.type === "text" && "text" in i), a = [...e.filter((i) => i.type !== "text")];
  if (t.length > 0) {
    const i = t.map((l) => "text" in l ? l.text : "").join(`

`);
    a.push({
      role: "user",
      type: "text",
      text: i,
      timestamp: w()
    });
  }
  return a;
}
function D(r) {
  return r instanceof DOMException && r.name === "AbortError" || r != null && typeof r == "object" && "name" in r && r.name === "CanceledError";
}
function O(r) {
  return {
    role: "user",
    type: "tool_response",
    tool_responses: r.map((e) => ({
      tool_call_id: e.id,
      name: e.name,
      response: { result: { status: "rejected" }, error: null },
      finished: !0
    })),
    timestamp: w()
  };
}
function E(r) {
  const e = {};
  for (const t of r)
    e[t.name] = {
      type: "server",
      name: t.name,
      description: t.description,
      requestSchema: t.request_json_schema,
      responseSchema: t.response_json_schema
    };
  return e;
}
export {
  K as CopilotSession,
  z as findToolCallById,
  q as prepareContextForTransport,
  P as prepareInstructionsForTransport,
  R as prepareToolsForTransport,
  E as serverSideToolsToCopilotTools,
  T as stringifyForCopilot,
  S as undent,
  A as updatedStatus
};
